// Handles interaction layer for address lookup.
// Adapted from Klimadatastyrelsen's own component.
// Everything about the API lives in addressLookup.js.

import {
    getAddress,
    isAddressSuggestion,
    isFinalSelection,
    isStreet,
    searchAddresses,
    toAddressFields,
    toStreetPrefill
} from './addressLookup.js'

const MAX_SUGGESTIONS = 10
const DEFAULT_DEBOUNCE_MS = 200

let instanceCount = 0

export default function attachAddressAutocomplete(inputElement, options = {}) {
    const {
        onSelect,
        onError,
        debounceMs = DEFAULT_DEBOUNCE_MS,
        emptyText = 'Ingen adresser fundet',
        loadingText = 'Søger …'
    } = options

    instanceCount += 1
    const listId = `kb-address-suggestions-${instanceCount}`

    const wrapper = inputElement.parentNode
    const list = document.createElement('ul')
    list.id = listId
    list.className = 'kb-address-suggestions'
    list.setAttribute('role', 'listbox')
    list.setAttribute('aria-label', 'Adresseforslag')
    list.hidden = true
    wrapper.append(list)

    inputElement.setAttribute('role', 'combobox')
    inputElement.setAttribute('aria-autocomplete', 'list')
    inputElement.setAttribute('aria-controls', listId)
    inputElement.setAttribute('aria-expanded', 'false')

    let suggestions = []
    let activeIndex = -1
    let debounceTimer
    let latestSearch = 0

    function isOpen() {
        return !list.hidden
    }

    function open() {
        list.style.top = `${inputElement.offsetTop + inputElement.offsetHeight}px`
        list.hidden = false
        inputElement.setAttribute('aria-expanded', 'true')
    }

    function close() {
        list.hidden = true
        list.replaceChildren()
        suggestions = []
        activeIndex = -1
        inputElement.setAttribute('aria-expanded', 'false')
        inputElement.removeAttribute('aria-activedescendant')
    }

    function fail(error) {
        close()
        if (onError) {
            onError(error)
        } else {
            console.error(error)
        }
    }

    function highlight(index) {
        activeIndex = index

        list.querySelectorAll('li').forEach((item, itemIndex) => {
            const active = itemIndex === index
            item.classList.toggle('kb-address-suggestion--active', active)
            item.setAttribute('aria-selected', active ? 'true' : 'false')

            if (active) {
                item.scrollIntoView({ block: 'nearest' })
            }
        })

        if (index < 0) {
            inputElement.removeAttribute('aria-activedescendant')
            return
        }

        inputElement.setAttribute(
            'aria-activedescendant',
            `${listId}-option-${index}`
        )
    }

    function showMessage(text) {
        const message = document.createElement('li')
        message.className = 'kb-address-suggestions__message'
        message.textContent = text

        list.replaceChildren(message)
        open()
        suggestions = []
        activeIndex = -1
        inputElement.removeAttribute('aria-activedescendant')
    }

    function render(items) {
        const addresses = items.filter(isAddressSuggestion)
        const rows = addresses.length ? addresses : items
        suggestions = rows

        if (!rows.length) {
            showMessage(emptyText)
            return
        }

        list.replaceChildren(
            ...rows.map((item, index) => {
                const option = document.createElement('li')
                option.id = `${listId}-option-${index}`
                option.className = 'kb-address-suggestion'
                option.setAttribute('role', 'option')
                option.setAttribute('aria-selected', 'false')
                option.textContent = item.titel

                option.addEventListener('mousedown', (event) => {
                    event.preventDefault()
                    select(item)
                })

                return option
            })
        )

        open()
        highlight(-1)
    }

    async function search(text) {
        const searchNumber = (latestSearch += 1)

        try {
            const items = await searchAddresses(text, {
                maksimum: MAX_SUGGESTIONS
            })

            if (searchNumber === latestSearch) {
                render(items)
            }
        } catch (error) {
            if (searchNumber === latestSearch) {
                fail(error)
            }
        }
    }

    async function select(item) {
        if (isStreet(item)) {
            const { value, caret } = toStreetPrefill(item)

            inputElement.value = value
            inputElement.focus()
            inputElement.setSelectionRange(caret, caret)

            showMessage(loadingText)
            await refine(value)
            return
        }

        if (!isFinalSelection(item)) {
            inputElement.value = item.titel
            showMessage(loadingText)
            await refine(item.titel)
            return
        }

        const searchNumber = (latestSearch += 1)

        showMessage(loadingText)

        try {
            const address = toAddressFields(await getAddress(item.id))

            if (searchNumber !== latestSearch) {
                return
            }

            close()

            if (!address) {
                return
            }

            inputElement.value = address.addressLine

            if (onSelect) {
                onSelect(address)
            }
        } catch (error) {
            if (searchNumber === latestSearch) {
                fail(error)
            }
        }
    }

    async function refine(text) {
        const searchNumber = (latestSearch += 1)

        try {
            const items = await searchAddresses(text, {
                maksimum: MAX_SUGGESTIONS
            })

            if (searchNumber !== latestSearch) {
                return
            }

            const addresses = items.filter(isAddressSuggestion)

            if (addresses.length === 1 && isFinalSelection(addresses[0])) {
                await select(addresses[0])
                return
            }

            render(items)
        } catch (error) {
            if (searchNumber === latestSearch) {
                fail(error)
            }
        }
    }

    function move(step) {
        if (!suggestions.length) {
            return
        }

        if (activeIndex < 0) {
            highlight(step > 0 ? 0 : suggestions.length - 1)
            return
        }

        highlight((activeIndex + step + suggestions.length) % suggestions.length)
    }

    function onInput() {
        clearTimeout(debounceTimer)

        const text = inputElement.value.trim()

        if (!text) {
            latestSearch += 1
            close()
            return
        }

        debounceTimer = setTimeout(() => search(text), debounceMs)
    }

    function onKeyDown(event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (!isOpen()) return
            event.preventDefault()
            move(event.key === 'ArrowDown' ? 1 : -1)
            return
        }

        if (event.key === 'Enter') {
            if (!isOpen() || !suggestions.length) return
            event.preventDefault()
            select(suggestions[activeIndex < 0 ? 0 : activeIndex])
            return
        }

        if (event.key === 'Escape' && isOpen()) {
            event.preventDefault()
            clearTimeout(debounceTimer)
            latestSearch += 1
            close()
        }
    }

    // Clicking back into a field that already holds an address reopens the
    // suggestions for what is there, so the address can be changed without
    // clearing the field first.
    function onFocusOrClick() {
        const text = inputElement.value.trim()

        if (!text || isOpen()) {
            return
        }

        search(text)
    }

    function onDocumentMouseDown(event) {
        if (!wrapper.contains(event.target)) {
            close()
        }
    }

    inputElement.addEventListener('input', onInput)
    inputElement.addEventListener('keydown', onKeyDown)
    inputElement.addEventListener('focus', onFocusOrClick)
    inputElement.addEventListener('click', onFocusOrClick)
    document.addEventListener('mousedown', onDocumentMouseDown)

    return function destroy() {
        clearTimeout(debounceTimer)
        inputElement.removeEventListener('input', onInput)
        inputElement.removeEventListener('keydown', onKeyDown)
        inputElement.removeEventListener('focus', onFocusOrClick)
        inputElement.removeEventListener('click', onFocusOrClick)
        document.removeEventListener('mousedown', onDocumentMouseDown)
        list.remove()
        inputElement.removeAttribute('role')
        inputElement.removeAttribute('aria-autocomplete')
        inputElement.removeAttribute('aria-controls')
        inputElement.removeAttribute('aria-expanded')
        inputElement.removeAttribute('aria-activedescendant')
    }
}
